/**
 * 🧪 SIMULATION SERVICE — Simulação de Cenários
 *
 * Serviço para o módulo Simulação:
 *  - CRUD de cenários
 *  - Execução de simulações (cálculo de throughput, lead time, etc.)
 *  - Filtragem por organização, status
 *
 * Nota: Fase 1 usa cálculos client-side (computeSimulation).
 *       Fase 2 poderá delegar a edge functions para modelos complexos.
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../lib/supabaseClient';
import type { SimulationScenario, ScenarioStatus } from '../types/simulation';
import { computeSimulation } from '../types/simulation';

export interface SimulationFilters {
  status?: ScenarioStatus;
  project_id?: string;
  search?: string;
}

export class SimulationService {
  static async list(organizationId: string, filters?: SimulationFilters): Promise<SimulationScenario[]> {
    let query = supabase
      .from('simulation_scenarios')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.project_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.project_id)) query = query.eq('project_id', filters.project_id);
    if (filters?.search) query = query.ilike('name', `%${filters.search}%`);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as SimulationScenario[];
  }

  static async getById(organizationId: string, scenarioId: string): Promise<SimulationScenario | null> {
    const { data, error } = await supabase
      .from('simulation_scenarios')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', scenarioId)
      .single();

    if (error) throw error;
    return data as SimulationScenario | null;
  }

  static async create(organizationId: string, scenario: Partial<Omit<SimulationScenario, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>): Promise<SimulationScenario> {
    const { data, error } = await supabase
      .from('simulation_scenarios')
      .insert({ ...scenario, organization_id: organizationId })
      .select()
      .single();

    if (error) throw error;
    return data as SimulationScenario;
  }

  static async update(organizationId: string, scenarioId: string, updates: Partial<SimulationScenario>): Promise<SimulationScenario> {
    const { data, error } = await supabase
      .from('simulation_scenarios')
      .update(updates)
      .eq('organization_id', organizationId)
      .eq('id', scenarioId)
      .select()
      .single();

    if (error) throw error;
    return data as SimulationScenario;
  }

  static async delete(organizationId: string, scenarioId: string): Promise<void> {
    const { error } = await supabase
      .from('simulation_scenarios')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', scenarioId);

    if (error) throw error;
  }

  /** Executa simulação: calcula outputs a partir dos inputs e persiste */
  static async runSimulation(organizationId: string, scenarioId: string): Promise<SimulationScenario> {
    const scenario = await this.getById(organizationId, scenarioId);
    if (!scenario) throw new Error('Scenario not found');
    if (!scenario.input) throw new Error('Scenario has no input parameters');

    await this.update(organizationId, scenarioId, { status: 'running' });

    try {
      const output = computeSimulation(scenario.input);
      return await this.update(organizationId, scenarioId, { output, status: 'completed' });
    } catch (err) {
      await this.update(organizationId, scenarioId, { status: 'failed' });
      throw err;
    }
  }
}
